import type { BadgeProps } from '@commenda/kakashi';

import {
  IServiceAuditorReport,
  IServiceAuditorStage,
  ServiceAuditorDisplayStatus,
  ServiceAuditorStageName,
  ServiceAuditorStageStatus,
} from '@commenda/api/agent-platform/service-auditor/types';
import {
  TRuntimeWorkflowStepExecution,
  WorkflowRuntimeStepStatus,
} from '@commenda/types';
import { pluralize } from '@commenda/utils';
import { TaskParty, TaskStatus } from '@prisma/client';

export enum ServiceAuditCardState {
  LOADING = 'LOADING',
  LOAD_ERROR = 'LOAD_ERROR',
  WAITING_FOR_DRAFT = 'WAITING_FOR_DRAFT',
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface IServiceAuditCardStateParams {
  hasAudits: boolean;
  isAuditListLoading: boolean;
  auditListError: unknown;
  report: IServiceAuditorReport | null;
  reportError: unknown;
}

export interface IServiceAuditStatusBadge {
  label: string;
  variant: BadgeProps['variant'];
  hasDot: boolean;
}

export interface IServiceAuditStageRowCopy {
  label: string;
  runningDetail: string;
}

export interface IServiceAuditRunStageRow {
  name: ServiceAuditorStageName;
  label: string;
  status: ServiceAuditorStageStatus;
  detail: string | null;
}

export interface IServiceAuditClientStatusParams {
  auditorStepStatus: WorkflowRuntimeStepStatus | null;
  // The wait step after the auditor; null when the definition has none.
  watchStep: { status: WorkflowRuntimeStepStatus | null } | null;
}

type TStepExecutions = Readonly<
  Record<string, TRuntimeWorkflowStepExecution> | undefined
>;

export class ServiceAuditRunStatusHelper {
  // Matches the backend auditor routes: admin-app users and agent-app users of the request's assigned firm.
  // Other agent firms get a 403, which the cards show as the neutral view.
  static readonly AUDIT_DETAIL_ROLES: ReadonlyArray<TaskParty> = [
    TaskParty.ADMIN,
    TaskParty.AGENT,
  ];

  // 403: the server does not count this user as a reviewer on the request. 404: the auditor flag is off.
  static readonly NEUTRAL_VIEW_ERROR_STATUSES: ReadonlyArray<number> = [
    403, 404,
  ];

  static readonly PENDING_AUDIT_REFRESH_INTERVAL_MS = 5000;

  static readonly QUEUED_DETAIL = 'Audit queued. It starts in a moment.';

  static readonly STATUS_LOAD_ERROR =
    'Could not load the audit status. Refresh the page to try again.';

  static readonly REPORT_LOAD_ERROR =
    'Could not load the audit report. Refresh the page to try again. If it still fails, review the draft by hand before approving.';

  static readonly CLIENT_IN_REVIEW_LABEL = 'In review';

  static readonly CLIENT_COMPLETED_LABEL = 'Completed';

  static readonly STAGE_ORDER: ReadonlyArray<ServiceAuditorStageName> = [
    ServiceAuditorStageName.GATHER,
    ServiceAuditorStageName.AUDIT,
    ServiceAuditorStageName.PROOF_CHECK,
    ServiceAuditorStageName.RANK,
  ];

  static readonly STAGE_ROWS: Readonly<
    Record<ServiceAuditorStageName, IServiceAuditStageRowCopy>
  > = {
    [ServiceAuditorStageName.GATHER]: {
      label: 'Gather inputs',
      runningDetail: 'Reading the workflow, form answers, threads and files',
    },
    [ServiceAuditorStageName.AUDIT]: {
      label: 'Audit the draft',
      runningDetail:
        "Checking the draft against the workflow, the customer's answers and Commenda records.",
    },
    [ServiceAuditorStageName.PROOF_CHECK]: {
      label: 'Check the quotes',
      runningDetail: 'Dropping any finding whose quotes are not in the files',
    },
    [ServiceAuditorStageName.RANK]: {
      label: 'Rank findings',
      runningDetail: 'Putting penalty findings first',
    },
  };

  static readonly STAGE_STATUS_WORDS: Readonly<
    Record<ServiceAuditorStageStatus, string>
  > = {
    [ServiceAuditorStageStatus.PENDING]: 'Pending',
    [ServiceAuditorStageStatus.RUNNING]: 'Running',
    [ServiceAuditorStageStatus.COMPLETED]: 'Completed',
    [ServiceAuditorStageStatus.FAILED]: 'Failed',
  };

  static readonly STATUS_BADGES: Readonly<
    Partial<Record<ServiceAuditCardState, IServiceAuditStatusBadge>>
  > = {
    [ServiceAuditCardState.WAITING_FOR_DRAFT]: {
      label: 'Waiting for the draft',
      variant: 'gray',
      hasDot: false,
    },
    [ServiceAuditCardState.QUEUED]: {
      label: 'Queued',
      variant: 'blue',
      hasDot: true,
    },
    [ServiceAuditCardState.RUNNING]: {
      label: 'Running',
      variant: 'blue',
      hasDot: true,
    },
    [ServiceAuditCardState.COMPLETED]: {
      label: 'Completed',
      variant: 'success',
      hasDot: true,
    },
    [ServiceAuditCardState.FAILED]: {
      label: 'Failed',
      variant: 'error',
      hasDot: true,
    },
  };

  private static readonly CARD_STATE_BY_DISPLAY_STATUS: Readonly<
    Record<ServiceAuditorDisplayStatus, ServiceAuditCardState>
  > = {
    [ServiceAuditorDisplayStatus.QUEUED]: ServiceAuditCardState.QUEUED,
    [ServiceAuditorDisplayStatus.RUNNING]: ServiceAuditCardState.RUNNING,
    [ServiceAuditorDisplayStatus.COMPLETED]: ServiceAuditCardState.COMPLETED,
    [ServiceAuditorDisplayStatus.FAILED]: ServiceAuditCardState.FAILED,
  };

  // The one switch for who sees audit details; the server still decides which agent firm may.
  static canSeeAuditDetails(role: TaskParty): boolean {
    return ServiceAuditRunStatusHelper.AUDIT_DETAIL_ROLES.includes(role);
  }

  // A running audit shows as in progress and a finished one as completed; a failed audit keeps the task status
  // here, because callers draw it with their own failed icon.
  static getAuditTaskStatus(
    auditStatus: ServiceAuditorDisplayStatus | null,
    taskStatus: TaskStatus
  ): TaskStatus {
    switch (auditStatus) {
      case ServiceAuditorDisplayStatus.QUEUED:
      case ServiceAuditorDisplayStatus.RUNNING:
        return TaskStatus.IN_PROGRESS;
      case ServiceAuditorDisplayStatus.COMPLETED:
        return TaskStatus.COMPLETED;
      default:
        return taskStatus;
    }
  }

  // A step that runs again adds one execution per pass, so the status is the newest attempt's.
  static getStepExecutionStatus(
    stepExecutions: TStepExecutions,
    stepDefinitionId: string | null | undefined
  ): WorkflowRuntimeStepStatus | null {
    if (!stepExecutions || !stepDefinitionId) return null;
    const latestStepExecution = Object.values(stepExecutions)
      .filter((candidate) => candidate.stepDefinitionId === stepDefinitionId)
      .reduce<TRuntimeWorkflowStepExecution | null>(
        (latest, candidate) =>
          !latest ||
          ServiceAuditRunStatusHelper.isLaterAttempt(candidate, latest)
            ? candidate
            : latest,
        null
      );
    return latestStepExecution?.status ?? null;
  }

  // Polls the audit list while the workflow sits on the auditor or its wait step, so a new audit can appear.
  static getAuditListRefreshIntervalMs(
    stepExecutions: TStepExecutions,
    stepDefinitionIds: ReadonlyArray<string | null>
  ): number {
    const isAuditPending = stepDefinitionIds.some(
      (stepDefinitionId) =>
        ServiceAuditRunStatusHelper.getStepExecutionStatus(
          stepExecutions,
          stepDefinitionId
        ) === WorkflowRuntimeStepStatus.IN_PROGRESS
    );
    return isAuditPending
      ? ServiceAuditRunStatusHelper.PENDING_AUDIT_REFRESH_INTERVAL_MS
      : 0;
  }

  // The report survives a failed poll, so it wins over any error and the card never guesses a status.
  static getCardState({
    hasAudits,
    isAuditListLoading,
    auditListError,
    report,
    reportError,
  }: Readonly<IServiceAuditCardStateParams>): ServiceAuditCardState {
    if (report) {
      return ServiceAuditRunStatusHelper.CARD_STATE_BY_DISPLAY_STATUS[
        report.displayStatus
      ];
    }
    if (auditListError) return ServiceAuditCardState.LOAD_ERROR;
    if (isAuditListLoading) return ServiceAuditCardState.LOADING;
    if (!hasAudits) return ServiceAuditCardState.WAITING_FOR_DRAFT;
    if (reportError) return ServiceAuditCardState.LOAD_ERROR;
    return ServiceAuditCardState.LOADING;
  }

  static getStatusBadge(
    cardState: ServiceAuditCardState
  ): IServiceAuditStatusBadge | null {
    return ServiceAuditRunStatusHelper.STATUS_BADGES[cardState] ?? null;
  }

  static getRunStageRows(
    stages: ReadonlyArray<IServiceAuditorStage>
  ): IServiceAuditRunStageRow[] {
    return ServiceAuditRunStatusHelper.STAGE_ORDER.map((name) => {
      const stage = stages.find((candidate) => candidate.name === name);
      return {
        name,
        label: ServiceAuditRunStatusHelper.STAGE_ROWS[name].label,
        status: stage?.status ?? ServiceAuditorStageStatus.PENDING,
        detail: stage
          ? ServiceAuditRunStatusHelper.getStageDetail(stage)
          : null,
      };
    });
  }

  // A failed audit still reads as in review: the draft waits for Commenda's approval.
  static getClientStatusLabel({
    auditorStepStatus,
    watchStep,
  }: Readonly<IServiceAuditClientStatusParams>): string {
    const isAuditFinished =
      auditorStepStatus === WorkflowRuntimeStepStatus.COMPLETED &&
      (watchStep === null ||
        watchStep.status === WorkflowRuntimeStepStatus.COMPLETED);
    return isAuditFinished
      ? ServiceAuditRunStatusHelper.CLIENT_COMPLETED_LABEL
      : ServiceAuditRunStatusHelper.CLIENT_IN_REVIEW_LABEL;
  }

  // An auditor endpoint error that means this user gets the neutral view, not the load error.
  static isNeutralViewError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      typeof error.status === 'number' &&
      ServiceAuditRunStatusHelper.NEUTRAL_VIEW_ERROR_STATUSES.includes(
        error.status
      )
    );
  }

  private static getStageDetail(
    stage: Readonly<IServiceAuditorStage>
  ): string | null {
    if (stage.status === ServiceAuditorStageStatus.RUNNING) {
      const { runningDetail } =
        ServiceAuditRunStatusHelper.STAGE_ROWS[stage.name];
      if (stage.name !== ServiceAuditorStageName.AUDIT) return runningDetail;
      return `${runningDetail} ${pluralize(stage.childToolCallCount, 'read so far', 'reads so far')}`;
    }
    if (stage.status === ServiceAuditorStageStatus.COMPLETED) {
      return ServiceAuditRunStatusHelper.getCompletedStageDetail(stage);
    }
    return null;
  }

  // Stage outputs come from stored JSON, so a count can arrive as null or missing; neither may render as text.
  private static isCount(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private static isLaterAttempt(
    candidate: Readonly<TRuntimeWorkflowStepExecution>,
    latest: Readonly<TRuntimeWorkflowStepExecution>
  ): boolean {
    if (candidate.attempt !== latest.attempt) {
      return candidate.attempt > latest.attempt;
    }
    return (candidate.startedAt ?? '') > (latest.startedAt ?? '');
  }

  private static getCompletedStageDetail(
    stage: Readonly<IServiceAuditorStage>
  ): string | null {
    const { output } = stage;
    switch (stage.name) {
      case ServiceAuditorStageName.GATHER:
        if (
          !ServiceAuditRunStatusHelper.isCount(output.files) ||
          !ServiceAuditRunStatusHelper.isCount(output.formAnswerBlocks)
        )
          return null;
        return `${pluralize(output.files, 'file')} and ${pluralize(output.formAnswerBlocks, 'form answer')} read`;
      case ServiceAuditorStageName.AUDIT:
        if (!ServiceAuditRunStatusHelper.isCount(output.candidates))
          return null;
        return pluralize(output.candidates, 'possible finding');
      case ServiceAuditorStageName.RANK:
        if (!ServiceAuditRunStatusHelper.isCount(output.ranked)) return null;
        return `${pluralize(output.ranked, 'finding')} ranked`;
      default:
        return null;
    }
  }
}
